/**
 * CheckoutPage starts payment process and therefore it will get data from ListingPage
 * (booking dates, listing data, and all the other data that affects to booking decision).
 * This data is saved to Session Store which only exists while the browsing session exists -
 * e.g. tab is open. (Session Store is not related to session cookies.)
 */
import Decimal from 'decimal.js';

import { isAfterDate, subtractTime } from '../../util/dates';
import { createSlug } from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';
import { types as sdkTypes } from '../../util/sdkLoader';
import { getProcess } from '../../transactions/transaction';

const { UUID, Money } = sdkTypes;
export const CHECKOUT_PAGE_SESSION_STORAGE_KEY = 'CheckoutPage';
export const CHECKOUT_PAGE_LOCAL_STORAGE_KEY = 'CheckoutPageLocal';
export const CHECKOUT_RETURN_PATH_KEY = 'CheckoutPageReturnPath';

// Validate that given 'obj' has all the keys of defined by validPropTypes parameter
// and values must pass related test-value-format function.
const validateProperties = (obj, validPropTypes) => {
  return Object.entries(validPropTypes).reduce((acc, [prop, fn]) => {
    if (Object.prototype.hasOwnProperty.call(obj, prop) && fn(obj[prop])) {
      return acc;
    }
    return false;
  }, true);
};

// Validate content of booking dates object received from SessionStore
export const isValidBookingDates = bookingDates => {
  const props = {
    bookingStart: d => d instanceof Date,
    bookingEnd: d => d instanceof Date,
  };
  return validateProperties(bookingDates, props);
};

// Validate content of listing object received from SessionStore.
// Currently only id & attributes.price are needed.
export const isValidListing = listing => {
  const { unitType } = listing?.attributes?.publicData || {};
  const isNegotiation = ['request', 'offer'].includes(unitType);
  const props = {
    id: id => id instanceof UUID,
    attributes: v => {
      return (
        typeof v === 'object' && (isNegotiation || (!isNegotiation && v.price instanceof Money))
      );
    },
  };
  return validateProperties(listing, props);
};

// Validate content of an transaction received from SessionStore.
// An id is required and the last transition needs to be one of the known transitions
export const isValidTransaction = transaction => {
  let process = null;
  try {
    const processName = transaction?.attributes?.processName;
    process = getProcess(processName);
  } catch (e) {
    console.error(
      'Transaction, found from sessionStorage, was following unsupported transaction process.'
    );
    return false;
  }

  const props = {
    id: id => id instanceof UUID,
    type: type => type === 'transaction',
    attributes: v => {
      return typeof v === 'object' && Object.values(process.transitions).includes(v.lastTransition);
    },
  };
  return validateProperties(transaction, props);
};

// Stores given bookingData, listing, transaction, and optional checkout state to sessionStorage
const serializeCheckoutData = (orderData, listing, transaction, checkoutState) => {
  const data = {
    orderData,
    listing,
    transaction,
    checkoutState,
    storedAt: new Date(),
  };

  const replacer = function(k, v) {
    if (this[k] instanceof Date) {
      return { date: v, _serializedType: 'SerializableDate' };
    }
    if (this[k] instanceof Decimal) {
      return { decimal: v, _serializedType: 'SerializableDecimal' };
    }
    return sdkTypes.replacer(k, v);
  };

  return JSON.stringify(data, replacer);
};

const reviver = (k, v) => {
  if (v && typeof v === 'object' && v._serializedType === 'SerializableDate') {
    return new Date(v.date);
  }
  if (v && typeof v === 'object' && v._serializedType === 'SerializableDecimal') {
    return new Decimal(v.decimal);
  }
  return sdkTypes.reviver(k, v);
};

const parseStoredCheckoutData = rawData => {
  if (!rawData) {
    return {};
  }

  const { orderData, listing, transaction, checkoutState, storedAt } = JSON.parse(rawData, reviver);
  const bookingDates = orderData?.bookingDates;
  const isPotentiallyIncludedBookingDatesValid = bookingDates
    ? isValidBookingDates(bookingDates)
    : true;
  const isFreshlySaved = storedAt
    ? isAfterDate(storedAt, subtractTime(new Date(), 1, 'days'))
    : false;
  const isTransactionValid = !!transaction ? isValidTransaction(transaction) : true;
  const isStoredDataValid =
    isFreshlySaved &&
    isPotentiallyIncludedBookingDatesValid &&
    isValidListing(listing) &&
    isTransactionValid;

  return isStoredDataValid ? { orderData, listing, transaction, checkoutState } : {};
};

export const storeData = (orderData, listing, transaction, storageKey, checkoutState = null) => {
  if (typeof window !== 'undefined' && window.sessionStorage && listing && orderData) {
    const storableData = serializeCheckoutData(orderData, listing, transaction, checkoutState);
    window.sessionStorage.setItem(storageKey, storableData);
  }
};

export const storePersistentCheckoutData = (
  orderData,
  listing,
  transaction,
  storageKey,
  checkoutState = null
) => {
  if (typeof window === 'undefined' || !listing || !orderData) {
    return;
  }

  const storableData = serializeCheckoutData(orderData, listing, transaction, checkoutState);

  if (window.sessionStorage) {
    window.sessionStorage.setItem(storageKey, storableData);
  }

  if (window.localStorage) {
    window.localStorage.setItem(CHECKOUT_PAGE_LOCAL_STORAGE_KEY, storableData);
  }
};

// Get stored data
export const storedData = storageKey => {
  if (typeof window === 'undefined') {
    return {};
  }

  const sessionData =
    window.sessionStorage && parseStoredCheckoutData(window.sessionStorage.getItem(storageKey));
  if (sessionData?.listing?.id) {
    return sessionData;
  }

  const localData =
    window.localStorage &&
    parseStoredCheckoutData(window.localStorage.getItem(CHECKOUT_PAGE_LOCAL_STORAGE_KEY));
  if (localData?.listing?.id) {
    storeData(
      localData.orderData,
      localData.listing,
      localData.transaction,
      storageKey,
      localData.checkoutState
    );
    return localData;
  }

  return {};
};

/**
 * Returns the checkout page path for a stored checkout session, if one exists.
 *
 * @param {String} storageKey key for the sessionStorage
 * @param {Array} routeConfiguration application route configuration
 * @returns {String|null} checkout path or null
 */
export const getStoredCheckoutPath = (storageKey, routeConfiguration) => {
  const storedPath = getCheckoutReturnPath();
  if (storedPath) {
    return storedPath;
  }

  const { listing } = storedData(storageKey);
  if (!listing?.id || !listing?.attributes?.title) {
    return null;
  }

  return createResourceLocatorString(
    'CheckoutPage',
    routeConfiguration,
    {
      id: listing.id.uuid,
      slug: createSlug(listing.attributes.title),
    },
    {}
  );
};

export const clearData = storageKey => {
  if (typeof window !== 'undefined' && window.sessionStorage) {
    window.sessionStorage.removeItem(storageKey);
  }
  if (typeof window !== 'undefined' && window.localStorage) {
    window.localStorage.removeItem(CHECKOUT_PAGE_LOCAL_STORAGE_KEY);
  }
  clearCheckoutReturnPath();
};

export const saveCheckoutReturnPath = path => {
  if (typeof window !== 'undefined' && window.localStorage && path) {
    window.localStorage.setItem(CHECKOUT_RETURN_PATH_KEY, path);
  }
};

export const getCheckoutReturnPath = () => {
  if (typeof window !== 'undefined' && window.localStorage) {
    return window.localStorage.getItem(CHECKOUT_RETURN_PATH_KEY);
  }
  return null;
};

export const clearCheckoutReturnPath = () => {
  if (typeof window !== 'undefined' && window.localStorage) {
    window.localStorage.removeItem(CHECKOUT_RETURN_PATH_KEY);
  }
};

export const createCheckoutPageInitialValuesAction = (
  setInitialValues,
  initialValues,
  saveToSessionStorage = false
) => {
  if (saveToSessionStorage) {
    const { listing, orderData, transaction, checkoutState } = initialValues || {};
    storeData(
      orderData,
      listing,
      transaction || null,
      CHECKOUT_PAGE_SESSION_STORAGE_KEY,
      checkoutState
    );
  }

  return setInitialValues(initialValues);
};

/**
 * Save page data to sessionstorage if the data is passed through navigation
 *
 * @param {Object} pageData an object containing orderData, listing and transaction entities.
 * @param {String} storageKey key for the sessionStorage
 * @param {Object} history navigation related object with pushState action
 * @returns pageData
 */
export const handlePageData = (
  { orderData, listing, transaction, checkoutState },
  storageKey,
  history
) => {
  // Browser's back navigation should not rewrite data in session store.
  // Action is 'POP' on both history.back() and page refresh cases.
  // Action is 'PUSH' when user has directed through a link
  // Action is 'REPLACE' when user has directed through login/signup process
  const hasNavigatedThroughLink = history.action === 'PUSH' || history.action === 'REPLACE';

  const hasDataInProps = !!(orderData && listing && hasNavigatedThroughLink);
  if (hasDataInProps) {
    // Store data only if data is passed through props and user has navigated through a link.
    storeData(orderData, listing, transaction, storageKey, checkoutState);
  }

  // NOTE: stored data can be empty if user has already successfully completed transaction.
  const pageData = hasDataInProps
    ? { orderData, listing, transaction, checkoutState }
    : storedData(storageKey);
  return pageData;
};
